import { lazy, Suspense, useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { titleFor } from "./utils/pageTitle";
import { useAuthStore } from "./state/authStore";
import { useTeamStore } from "./state/teamStore";
import ProtectedRoute from "./components/ProtectedRoute";
import AppShell from "./components/AppShell";
import PageFallback from "./components/PageFallback";
import ErrorBoundary from "./components/ErrorBoundary";

// Route-level code-splitting: every page is a lazy chunk so the initial download is
// the shell + router + stores, not the whole analysis suite (uPlot, html2canvas) that a
// cold prospect on the landing page never needs. Suspense boundaries live inside the
// layouts (App / AppShell / WeekWorkspace) so the shell + tool tabs stay put on nav.
const Landing = lazy(() => import("./pages/Landing"));
const Login = lazy(() => import("./pages/Login"));
const RunDetail = lazy(() => import("./pages/RunDetail"));
const MasterComparison = lazy(() => import("./pages/MasterComparison"));
const HeadToHead = lazy(() => import("./pages/HeadToHead"));
const TeamSettings = lazy(() => import("./pages/TeamSettings"));
const JoinTeam = lazy(() => import("./pages/JoinTeam"));
const FuelPlanner = lazy(() => import("./pages/FuelPlanner"));
const HandlingPanel = lazy(() => import("./pages/HandlingPanel"));
const StintsPanel = lazy(() => import("./pages/StintsPanel"));
const PitAdjustments = lazy(() => import("./pages/PitAdjustments"));
const Checklist = lazy(() => import("./pages/Checklist"));
const ChecklistTemplates = lazy(() => import("./pages/ChecklistTemplates"));
const Settings = lazy(() => import("./pages/Settings"));
const WeekWorkspace = lazy(() => import("./pages/WeekWorkspace"));
const WeekOverview = lazy(() => import("./pages/WeekOverview"));
const TeamsHome = lazy(() => import("./pages/TeamsHome"));
const TeamHome = lazy(() => import("./pages/TeamHome"));
const SeriesHome = lazy(() => import("./pages/SeriesHome"));
const TeamOnboarding = lazy(() => import("./pages/TeamOnboarding"));
const DriversBoard = lazy(() => import("./pages/DriversBoard"));
const QualifyingBoard = lazy(() => import("./pages/QualifyingBoard"));
const DesktopAgent = lazy(() => import("./pages/DesktopAgent"));
const CrewChief = lazy(() => import("./pages/CrewChief"));
const StreamOverlay = lazy(() => import("./pages/StreamOverlay"));
const InSimOverlay = lazy(() => import("./pages/InSimOverlay"));
const SetupLibrary = lazy(() => import("./pages/SetupLibrary"));
const PitLog = lazy(() => import("./pages/PitLog"));
const SetupDiff = lazy(() => import("./pages/SetupDiff"));
const Trends = lazy(() => import("./pages/Trends"));
const TrackTempModelHealth = lazy(() => import("./pages/TrackTempModelHealth"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Signup = lazy(() => import("./pages/Signup"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const AdminConsole = lazy(() => import("./pages/AdminConsole"));
const NotFound = lazy(() => import("./pages/NotFound"));

export default function App() {
  const { checkAuth, user } = useAuthStore();
  const { fetchTeams } = useTeamStore();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // #803: refetch teams when the signed-in IDENTITY changes, not every time the user
  // object is reloaded (a profile or avatar save): that refetch put the whole app
  // behind TeamOnboardingGate's spinner and remounted the page mid-save.
  const userId = user?.id;
  useEffect(() => {
    if (userId) fetchTeams();
  }, [userId, fetchTeams]);

  const { pathname } = useLocation();
  useEffect(() => {
    document.title = titleFor(pathname);
  }, [pathname]);

  return (
    // ErrorBoundary ABOVE the top-level Suspense so a lazy-chunk load failure (a stale
    // index.html hitting rotated hashes after a redeploy) shows the reload card, not a
    // blank screen, on the public routes too (the authed side already has one in AppShell).
    <ErrorBoundary>
    <Suspense fallback={<PageFallback />}>
      <Routes>
      <Route
        path="/"
        element={user ? <Navigate to="/teams" replace /> : <Landing />}
      />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      {/* OBS overlay: load its chunk INVISIBLY (null fallback) — never flash a spinner
          into a live stream source. */}
      <Route path="/stream-overlay" element={<Suspense fallback={null}><StreamOverlay /></Suspense>} />
      {/* In-sim overlay (#378): the same feed, rendered for a driver instead of an
          audience. Null fallback for the same reason — a spinner has no business
          flashing over a live session. */}
      <Route path="/insim-overlay" element={<Suspense fallback={null}><InSimOverlay /></Suspense>} />
      <Route path="/join/:token" element={<ProtectedRoute><JoinTeam /></ProtectedRoute>} />
      <Route
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route path="/admin" element={<AdminConsole />} />
        <Route path="/onboarding/team/new" element={<TeamOnboarding />} />
        <Route path="/teams" element={<TeamsHome />} />
        <Route path="/teams/:teamId" element={<TeamHome />} />
        <Route path="/teams/:teamId/settings" element={<TeamSettings />} />
        <Route path="/teams/:teamId/checklists" element={<ChecklistTemplates />} />
        <Route path="/teams/:teamId/model-health" element={<TrackTempModelHealth />} />
        <Route path="/teams/:teamId/setups" element={<SetupLibrary />} />
        <Route path="/teams/:teamId/pit-log" element={<PitLog />} />
        <Route path="/teams/:teamId/setups/compare" element={<SetupDiff />} />
        <Route path="/teams/:teamId/series/:seriesSlug" element={<SeriesHome />} />
        <Route path="/teams/:teamId/series/:seriesSlug/weeks/:trackWeekId" element={<WeekWorkspace />}>
          <Route index element={<Navigate to="overview" replace />} />
          <Route path="overview" element={<WeekOverview />} />
          <Route path="drivers" element={<DriversBoard />} />
          <Route path="qualifying" element={<QualifyingBoard />} />
          <Route path="master" element={<MasterComparison />} />
          <Route path="head-to-head" element={<HeadToHead />} />
          <Route path="fuel" element={<FuelPlanner />} />
          <Route path="stints" element={<StintsPanel />} />
          <Route path="handling" element={<HandlingPanel />} />
          <Route path="pit" element={<PitAdjustments />} />
          <Route path="checklist" element={<Checklist />} />
          <Route path="setup-diff" element={<Navigate to="../head-to-head" replace />} />
          <Route path="trends" element={<Trends />} />
        </Route>
        <Route path="/runs/:id" element={<RunDetail />} />
        <Route path="/teams/:teamId/live" element={<CrewChief />} />
        <Route path="/desktop" element={<DesktopAgent />} />
        <Route path="/settings" element={<Settings />} />
      </Route>
      <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
    </ErrorBoundary>
  );
}
